import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { Download, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { AuditEntry } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import type { PersonOption } from '../../../components/PersonPicker';
import { EmptyState, FilteredEmptyState, ForbiddenState, SystemState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanView } from '../../../stores/session';
import { useAuditExport, useAuditFeed, useAuditSummary } from '../api/audit-api';
import { useRoles } from '../api/roles-api';
import { useDepartments, useUsers, useVentures } from '../api/users-api';
import { filterCount, whenLabel, type AccessLogSearch } from '../components/access-log/access-log-address';
import type { AccessLogLookups } from '../components/access-log/access-log-words';
import { AccessLogDetail } from '../components/access-log/AccessLogDetail';
import { AccessLogFeed } from '../components/access-log/AccessLogFeed';
import { AccessLogFilters } from '../components/access-log/AccessLogFilters';
import styles from './AccessAuditPage.module.css';

/**
 * USER-06 — the access log. F2.13. Admin redesign 2026-11-02, Option A of `2-people/access-log.html`: the kinds with
 * their counts on the left, the feed by day in the middle, and the clicked entry explained in full on the right.
 *
 * It is a read of the event log, not a table of its own — non-negotiable #10. A separate audit store would be a
 * second version of the truth that could disagree with the first, and the whole value of an audit is that it cannot.
 * Every filter is in the address (06-qol §3), so a copied link shows a colleague the same feed and the same entry.
 */

/** Tablet and phone: one column, and the clicked entry opens as its own view (as drawn for 375px). */
const SINGLE_COLUMN = '(max-width: 900px)';
/** Enough for the "Who" list in one request; the server's own limit. */
const PEOPLE_PAGE = { page: 1, pageSize: 200 } as const;

export function AccessAuditPage(): ReactNode {
  const canView = useCanView('audit_log');
  const search = useSearch({ from: '/app-layout/admin/audit' });
  const navigate = useNavigate();
  const router = useRouter();
  const isSingleColumn = useMediaQuery(SINGLE_COLUMN);
  // The entry was opened from this feed on a phone, so "back" is the browser's Back, not a second history step.
  const [isOpenedHere, setIsOpenedHere] = useState(false);
  if (search.entry === undefined && isOpenedHere) setIsOpenedHere(false);

  const feed = useAuditFeed(search, canView);
  const summary = useAuditSummary(search, canView);
  const exporter = useAuditExport();
  const users = useUsers(PEOPLE_PAGE, canView);
  // Review R22: only for someone who may open the log, or the visit asks for /roles and is refused.
  const roles = useRoles(canView);
  const ventures = useVentures();
  const departments = useDepartments();

  if (!canView) {
    return <ForbiddenState resource="the access log" contact="Your admin" />;
  }

  const entries = feed.data?.pages.flatMap((page) => page.rows) ?? [];
  const lookups: AccessLogLookups = {
    roleLabelOf: (id) => roles.data?.find((role) => role.id === id)?.label ?? null,
    roleKeyOf: (id) => {
      const role = roles.data?.find((candidate) => candidate.id === id);
      return role?.key ?? role?.baseRoleKey ?? null;
    },
    personNameOf: (id) => users.data?.rows.find((person) => person.id === id)?.fullName ?? null,
    placeNameOf: (id) => ventures.data?.find((venture) => venture.id === id)?.name ?? departments.data?.find((department) => department.id === id)?.name ?? null,
  };
  const people = peopleOf(users.data?.rows ?? [], search.actorId, entries);

  const go = (next: AccessLogSearch, replace = true): void => {
    void navigate({ to: '/admin/audit', search: next, replace });
  };
  const opened = entries.find((entry) => entry.id === search.entry) ?? null;
  // On a wide screen the newest entry is explained until another is clicked: one click fewer, nothing empty.
  const selected = opened ?? (isSingleColumn ? null : (entries[0] ?? null));

  const select = (entry: AuditEntry): void => {
    if (isSingleColumn) {
      setIsOpenedHere(true);
      go({ ...search, entry: entry.id }, false);
      return;
    }
    go({ ...search, entry: entry.id });
  };
  const back = (): void => {
    if (isOpenedHere) {
      setIsOpenedHere(false);
      router.history.back();
      return;
    }
    const { entry: _entry, ...rest } = search;
    go(rest);
  };

  if (isSingleColumn && opened) {
    return (
      <div className={styles.page}>
        <AccessLogDetail entry={opened} lookups={lookups} onBack={back} />
      </div>
    );
  }

  const hasFailed = feed.isError && !feed.data;
  const activeFilters = filterCount(search);
  const emptyState = hasFailed ? (
    <SystemState
      icon={TriangleAlert}
      tone="danger"
      title="The access log didn't load"
      body={errorMessage(feed.error)}
      actions={
        <Button variant="quiet" onClick={() => void feed.refetch()}>
          Try again
        </Button>
      }
      isInset
    />
  ) : activeFilters > 0 ? (
    <FilteredEmptyState filterCount={activeFilters} onClearFilters={() => go({})} isInset />
  ) : (
    <EmptyState
      title="Nothing recorded in the last 30 days"
      body="Sign-ins, refused actions, role changes and viewing as someone all appear here as they happen. Pick earlier dates under When to look further back."
      action={null}
      isInset
    />
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Access log</h1>
          <p className={styles.lede}>
            Who signed in, who was refused something, who changed someone&apos;s access, and who viewed the app as somebody else. Nothing here can be
            edited.
          </p>
        </div>
        <Button variant="outline" icon={Download} isPending={exporter.isPreparing} onClick={() => exporter.run(search)}>
          Export what you see
        </Button>
      </header>

      <div className={styles.layout}>
        <div className={styles.filtersArea}>
          <AccessLogFilters search={search} summary={summary.data} people={people} onChange={(next) => go(next)} />
        </div>

        <div className={styles.feedArea}>
          <AccessLogFeed
            entries={entries}
            lookups={lookups}
            selectedId={selected?.id ?? null}
            onSelect={select}
            isSelectOnMove={!isSingleColumn}
            q={search.q}
            onSearch={(q) => go({ ...search, q })}
            isFirstLoad={feed.isPending}
            hasMore={feed.hasNextPage}
            isLoadingMore={feed.isFetchingNextPage}
            onLoadMore={() => {
              if (!feed.isFetchingNextPage) void feed.fetchNextPage();
            }}
            whenText={whenLabel(search)}
            emptyState={emptyState}
          />
        </div>

        {!isSingleColumn && (
          <div className={styles.detailArea}>
            {selected ? (
              <AccessLogDetail entry={selected} lookups={lookups} />
            ) : (
              <p className={styles.pick}>{feed.isPending ? '' : 'Click an entry to see the full story.'}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Everyone, A to Z, for "Who". Someone picked from a link but missing from the list is kept, named from their entries. */
function peopleOf(
  rows: readonly { readonly id: string; readonly fullName: string; readonly email?: string }[],
  actorId: string | undefined,
  entries: readonly AuditEntry[],
): readonly PersonOption[] {
  const people: PersonOption[] = rows.map((row) => ({ id: row.id, fullName: row.fullName, ...(row.email ? { email: row.email } : {}) }));
  if (actorId && !people.some((person) => person.id === actorId)) {
    const name = entries.find((entry) => entry.actorId === actorId)?.actorName;
    people.unshift({ id: actorId, fullName: name ?? 'The person picked' });
  }
  return people;
}
